import React from 'react'
import styled from 'styled-components'
import { IcArrowRight } from '@healingpaper/cell-icons'
import Link from 'next/link'

import { ServiceOfferCard } from '@/features/service-offer/components/service-offer-card'
import { SearchPageEventImpressedEventParam } from '@/features/search/screen/search'

import type { EventListItemOnClickEventParam } from '@/components/pages/eventList/EventListItem'
import type { Countries } from '@/utils/language'
import type { SearchServiceOfferItem } from '@/features/search/api/search.types'

export interface Props {
  serviceOfferItems: SearchServiceOfferItem[]
  text: {
    specialPrice: string
    vat: string
    certifiedHospital: string
    realmodel: string
    moreButton?: string
  }
  onClickMore?: () => void
  onClickEvent: (event: EventModuleOnClickEventParam, index: number) => void
  onImpressedEvent?: (event: SearchPageEventImpressedEventParam, index: number) => void
  moreButtonLink?: string
}

export interface EventModuleOnClickEventParam {
  id: number
  title: string
  hospitalId: number
  discountedCost: number
  currency: Countries
  hospitalCountry: string
  hospitalSido: string
}

const EventModule = ({
  serviceOfferItems,
  text,
  onClickMore,
  onClickEvent,
  onImpressedEvent,
  moreButtonLink,
}: Props): JSX.Element => {
  const handleClickEvent = (event: EventListItemOnClickEventParam, index: number) => {
    onClickEvent(
      {
        id: event.id,
        title: event.title,
        hospitalId: event.hospitalId,
        discountedCost: event.discountedCost,
        currency: event.currency as Countries,
        hospitalCountry: event.hospitalCountry || '',
        hospitalSido: event.hospitalSido || '',
      },
      index,
    )
  }

  return (
    <div>
      <ul className="list-none m-0 p-0">
        {serviceOfferItems?.map((serviceOfferItem, index) => {
          const serviceOffer = serviceOfferItem.serviceOffer
          if (!serviceOffer) {
            return null
          }

          const {
            id: serviceOfferId,
            hospital: { id: hospitalId, district: hospitalSido, name: hospitalName },
            thumbnail,
            rating: { amount: ratingAmount, count: ratingCount },
            price: { originalAmount, discountedAmount, currency },
            representativeOption: {
              id: optionId,
              price: {
                originalAmount: representativeOriginalAmount,
                discountedAmount: representativeDiscountedAmount,
                currency: representativeCurrency,
                exchangeRateAppliedPrice,
              },
            },
            title,
          } = serviceOffer
          const finalDiscountedAmount =
            exchangeRateAppliedPrice?.discountedAmount || representativeDiscountedAmount || discountedAmount
          const finalOriginalAmount =
            exchangeRateAppliedPrice?.originalAmount || representativeOriginalAmount || originalAmount
          const finalCurrency = exchangeRateAppliedPrice?.currency || representativeCurrency || currency

          return (
            <li key={serviceOfferId}>
              <ServiceOfferCard
                elementId={`${serviceOfferId}`}
                eventId={serviceOfferId}
                title={title}
                thumbnailUrl={thumbnail}
                subtitle={`${hospitalSido}${hospitalName ? `・${hospitalName}` : ''} `}
                review={{
                  rating: ratingAmount,
                  count: ratingCount,
                }}
                price={{
                  originalCost: finalOriginalAmount,
                  discountedCost: finalDiscountedAmount,
                  currency: finalCurrency as string,
                }}
                optionId={optionId}
                titleElementType="h2"
                priceElementType="h3"
                trackClickEvent={() => {
                  handleClickEvent(
                    {
                      id: serviceOfferId,
                      title,
                      hospitalId,
                      cost: finalOriginalAmount,
                      discountRate: finalDiscountedAmount,
                      discountedCost: finalDiscountedAmount,
                      currency: finalCurrency as Countries,
                      hospitalCountry: '',
                      hospitalSido: hospitalSido || '',
                    },
                    index,
                  )
                }}
                onImpressed={() => {
                  onImpressedEvent?.(
                    {
                      id: serviceOfferId,
                      hospitalId,
                      cost: finalOriginalAmount,
                      currency: finalCurrency as string,
                    },
                    index,
                  )
                }}
              />
            </li>
          )
        })}
      </ul>

      {text.moreButton && onClickMore && (
        <div className="mt-6 px-6" data-testid="event-module-more-button-wrap">
          {typeof moreButtonLink === 'string' ? (
            <Link href={moreButtonLink}>
              <StyledButton onClick={onClickMore}>
                {text.moreButton}
                <IcArrowRight width={16} height={16} />
              </StyledButton>
            </Link>
          ) : (
            <StyledButton onClick={onClickMore}>
              {text.moreButton}
              <IcArrowRight width={16} height={16} />
            </StyledButton>
          )}
        </div>
      )}
    </div>
  )
}

const StyledButton = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  width: 100%;
  box-sizing: border-box;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.gray200};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.black};
  ${({ theme }) => theme.typography.title4}
`
export default EventModule
